/**
 * Mobile search takeover + keyboard scroll fix (iOS Safari & Chrome).
 *
 * Two separate problems on iPhone:
 * 1) Layout viewport stays full-height under the keyboard → flydown must be
 *    capped to visualViewport or the last items sit behind the keyboard.
 * 2) Safari skips painting the last in-flow child of an overflow scroller when
 *    an ancestor still has `transform` (even `translateY(0)`) and the keyboard
 *    is open. Flatten that transform after the takeover animation, avoid
 *    `-webkit-overflow-scrolling: touch`, and keep a painted dummy as the
 *    last child of the scroller.
 */
(function () {
  var MOBILE_BREAKPOINT = 720;
  var KEYBOARD_RATIO = 0.85;
  var MIN_HEIGHT = 100;
  var HEIGHT_VAR = '--live-search-flydown-height';
  var PAD_VAR = '--live-search-keyboard-pad';

  var viewport = window.visualViewport;
  var rafId = 0;
  var lastHeight = -1;
  var lastPad = -1;
  var isChromeIOS = /CriOS/i.test(navigator.userAgent);

  function isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  function isTakeoverActive() {
    return document.body.classList.contains('mobile-search-takeover-active');
  }

  function getTakeover() {
    return document.querySelector('.live-search--takeover');
  }

  function getFlydown() {
    var takeover = getTakeover();
    return takeover ? takeover.querySelector('[data-live-search-flydown]') : null;
  }

  function getInput() {
    var takeover = getTakeover();
    return takeover ? takeover.querySelector('[data-live-search-input]') : null;
  }

  function flattenTakeoverTransform() {
    var takeover = getTakeover();
    if (!takeover || takeover.getAttribute('data-animation')) return;
    takeover.style.transform = 'none';
    takeover.style.webkitTransform = 'none';
  }

  function clearTakeoverTransform() {
    var takeover = getTakeover();
    if (!takeover) return;
    takeover.style.removeProperty('transform');
    takeover.style.removeProperty('-webkit-transform');
  }

  function isInputFocused() {
    var input = getInput();
    return !!(input && document.activeElement === input);
  }

  function getKeyboardOverlap() {
    if (!viewport) return 0;

    var visibleBottom = viewport.offsetTop + viewport.height;
    var overlap = Math.max(0, Math.round(window.innerHeight - visibleBottom));

    if (viewport.height < window.innerHeight * KEYBOARD_RATIO) {
      return Math.max(overlap, 24);
    }

    return 0;
  }

  function clearStyles(flydown) {
    if (!flydown) return;
    flydown.style.removeProperty(HEIGHT_VAR);
    flydown.style.removeProperty(PAD_VAR);
    flydown.style.removeProperty('height');
    flydown.style.removeProperty('max-height');
    flydown.style.removeProperty('--open-height');
    flydown.style.removeProperty('--flydown-max-height');
    flydown.style.removeProperty('--live-search-paint-tail');
    flydown.classList.remove('live-search-flydown--height-synced');
    clearTakeoverTransform();
    lastHeight = -1;
    lastPad = -1;
  }

  function sizePaintTail(flydown) {
    var tail = flydown.querySelector('.search-flydown__paint-tail');
    if (!tail) return;

    var overflow = flydown.scrollHeight - flydown.clientHeight;
    var tailHeight = overflow > 80 ? 96 : 12;
    flydown.style.setProperty('--live-search-paint-tail', tailHeight + 'px');
  }

  /**
   * Rebuild the overflow compositor while the keyboard is open.
   * Long result lists still leave the last compositor tile unpainted; a painted
   * tail moves real content off that tile, and a hidden/scroll toggle recaptures size.
   */
  function forcePaintLastItems(flydown) {
    if (!flydown) return;

    flattenTakeoverTransform();
    sizePaintTail(flydown);

    var y = flydown.scrollTop;
    flydown.style.setProperty('-webkit-overflow-scrolling', 'auto');
    flydown.style.overflowY = 'hidden';
    void flydown.offsetHeight;
    flydown.style.overflowY = 'scroll';
    flydown.scrollTop = y;

    var tail = flydown.querySelector('.search-flydown__paint-tail');
    if (tail) {
      var current = tail.offsetHeight;
      tail.style.height = current + 1 + 'px';
      void flydown.scrollHeight;
      tail.style.height = '';
    }
  }

  function bindLastItemPaint(flydown) {
    if (!flydown || flydown.dataset.lastItemPaintBound) return;
    flydown.dataset.lastItemPaintBound = 'true';

    var paintQueued = false;
    function schedulePaint() {
      if (paintQueued || !isTakeoverActive()) return;
      paintQueued = true;
      window.requestAnimationFrame(function () {
        paintQueued = false;
        if (!isTakeoverActive()) return;
        forcePaintLastItems(flydown);
      });
    }

    flydown.addEventListener(
      'scroll',
      function () {
        if (!isTakeoverActive()) return;
        var remaining = flydown.scrollHeight - flydown.scrollTop - flydown.clientHeight;
        if (remaining < 180) schedulePaint();
      },
      { passive: true }
    );

    if (typeof ResizeObserver !== 'undefined') {
      var results = flydown.querySelector('[data-live-search-results]');
      if (results) {
        var lastResultsHeight = -1;
        var resizeObserver = new ResizeObserver(function () {
          var height = results.offsetHeight;
          if (Math.abs(height - lastResultsHeight) < 8) return;
          lastResultsHeight = height;
          schedulePaint();
        });
        resizeObserver.observe(results);
      }
    }
  }

  function observeLastContent(flydown) {
    bindLastItemPaint(flydown);
    if (!flydown || typeof IntersectionObserver === 'undefined') return;

    if (flydown._lastItemIO) flydown._lastItemIO.disconnect();

    var lastIoPaint = 0;
    var io = new IntersectionObserver(
      function (entries) {
        if (!isTakeoverActive()) return;
        var now = Date.now();
        if (now - lastIoPaint < 250) return;
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            lastIoPaint = now;
            forcePaintLastItems(flydown);
            break;
          }
        }
      },
      { root: flydown, threshold: 0.01, rootMargin: '0px 0px 64px 0px' }
    );

    flydown._lastItemIO = io;

    var results = flydown.querySelector('[data-live-search-results]');
    var continueBtn = results && results.querySelector('.search-flydown__continue');
    var tail = flydown.querySelector('.search-flydown__paint-tail');
    if (continueBtn) io.observe(continueBtn);
    if (tail) io.observe(tail);

    if (results) {
      results.querySelectorAll('img').forEach(function (img) {
        if (!img.complete) {
          img.addEventListener(
            'load',
            function () {
              if (isTakeoverActive()) forcePaintLastItems(flydown);
            },
            { once: true }
          );
        }
      });
    }
  }

  function applyStyles(flydown) {
    if (!isMobile() || !flydown || !isTakeoverActive()) {
      clearStyles(flydown);
      return;
    }

    var rect = flydown.getBoundingClientRect();
    var keyboardPad = getKeyboardOverlap();
    var visibleBottom = viewport
      ? Math.min(viewport.offsetTop + viewport.height, window.innerHeight)
      : window.innerHeight;
    var keyboardOpen = keyboardPad > 0;

    var nextHeight;
    var nextPad;

    if (keyboardOpen || (isInputFocused() && viewport)) {
      // Prefer the visible area above the keyboard whenever search is focused.
      var bottom = keyboardOpen ? visibleBottom : Math.min(visibleBottom, window.innerHeight);
      if (!keyboardOpen && viewport) {
        bottom = Math.min(viewport.offsetTop + viewport.height, window.innerHeight);
      }
      nextHeight = Math.max(MIN_HEIGHT, Math.round(bottom - rect.top - 8));
      nextPad = keyboardOpen ? 24 : 16;
    } else {
      nextHeight = Math.max(MIN_HEIGHT, Math.round(window.innerHeight - rect.top));
      nextPad = 0;
    }

    if (Math.abs(nextHeight - lastHeight) < 2 && Math.abs(nextPad - lastPad) < 2) {
      return;
    }

    var scrollTop = flydown.scrollTop;

    flydown.style.setProperty(HEIGHT_VAR, nextHeight + 'px');
    flydown.style.setProperty(PAD_VAR, nextPad + 'px');
    flydown.style.height = nextHeight + 'px';
    flydown.style.maxHeight = nextHeight + 'px';
    flydown.style.setProperty('--open-height', nextHeight + 'px');
    flydown.style.setProperty('--flydown-max-height', nextHeight + 'px');
    flydown.classList.add('live-search-flydown--height-synced');
    flattenTakeoverTransform();

    lastHeight = nextHeight;
    lastPad = nextPad;

    if (scrollTop > 0) {
      flydown.scrollTop = scrollTop;
    }
  }

  function sync() {
    rafId = 0;
    applyStyles(getFlydown());
  }

  function scheduleSync() {
    if (rafId) return;
    rafId = window.requestAnimationFrame(sync);
  }

  function forceSync() {
    lastHeight = -1;
    lastPad = -1;
    scheduleSync();
    window.setTimeout(scheduleSync, 50);
    window.setTimeout(scheduleSync, 150);
    window.setTimeout(scheduleSync, 350);
    window.setTimeout(scheduleSync, 700);
  }

  function updateDesktopFlydownHeight() {
    if (isTakeoverActive() || window.innerWidth < MOBILE_BREAKPOINT) return;

    document.querySelectorAll('[data-live-search-flydown]').forEach(function (flydown) {
      if (flydown.getAttribute('data-animation-state') !== 'open') return;

      var container = flydown.querySelector(':scope > .visible');
      if (!container) return;

      flydown.style.setProperty('--open-height', container.scrollHeight + 'px');

      var viewportHeight = document.documentElement.clientHeight;
      var topPos = container.getBoundingClientRect().top;
      var maxHeight = Math.max(160, viewportHeight - topPos - 30);
      flydown.style.setProperty('--flydown-max-height', maxHeight + 'px');
    });
  }

  function afterDesktopResultsLoaded() {
    if (isTakeoverActive() || window.innerWidth < MOBILE_BREAKPOINT) return;

    updateDesktopFlydownHeight();
    window.requestAnimationFrame(updateDesktopFlydownHeight);
    window.setTimeout(updateDesktopFlydownHeight, 50);
    window.setTimeout(updateDesktopFlydownHeight, 200);
    window.setTimeout(updateDesktopFlydownHeight, 450);

    document.querySelectorAll('[data-live-search-results] img').forEach(function (img) {
      if (!img.complete) {
        img.addEventListener('load', updateDesktopFlydownHeight, { once: true });
      }
    });
  }

  function afterResultsLoaded() {
    var flydown = getFlydown();
    if (!flydown) return;

    applyStyles(flydown);
    observeLastContent(flydown);
    forcePaintLastItems(flydown);
    forceSync();

    // Later passes after Empire animation / rimg, when long lists finish layout
    window.setTimeout(function () {
      applyStyles(flydown);
      forcePaintLastItems(flydown);
    }, 200);

    window.setTimeout(function () {
      applyStyles(flydown);
      forcePaintLastItems(flydown);
    }, 500);

    window.setTimeout(function () {
      if (!isTakeoverActive()) return;
      applyStyles(flydown);
      forcePaintLastItems(flydown);
    }, 1000);
  }

  function stabilizeChrome() {
    if (!isChromeIOS || !isTakeoverActive()) return;
    var input = getInput();
    if (input && document.activeElement === input) {
      input.blur();
    }
    forceSync();
  }

  function isFlydownTarget(target) {
    return target && target.closest && target.closest('[data-live-search-flydown]');
  }

  function blockBackgroundPan(event) {
    if (!isTakeoverActive()) return;
    if (isFlydownTarget(event.target)) return;
    event.preventDefault();
  }

  if (viewport) {
    var wasKeyboard = false;

    viewport.addEventListener('resize', function () {
      var nowKeyboard = getKeyboardOverlap() > 0;

      if (wasKeyboard && !nowKeyboard) {
        window.setTimeout(stabilizeChrome, 40);
      }

      wasKeyboard = nowKeyboard;
      forceSync();

      var flydown = getFlydown();
      if (flydown && isTakeoverActive()) {
        forcePaintLastItems(flydown);
      }
    });

    viewport.addEventListener('scroll', scheduleSync);
  }

  window.addEventListener('resize', forceSync);
  window.addEventListener('orientationchange', forceSync);

  document.addEventListener('focusin', function (event) {
    if (!event.target || !event.target.closest('[data-live-search]')) return;
    forceSync();
  });

  document.addEventListener('focusout', function (event) {
    if (!event.target || !event.target.closest('[data-live-search]')) return;
    forceSync();
  });

  document.addEventListener('touchmove', blockBackgroundPan, { passive: false });

  document.querySelectorAll('[data-live-search-flydown]').forEach(function (flydown) {
    if (flydown.dataset.mobileScrollBound) return;
    flydown.dataset.mobileScrollBound = 'true';

    flydown.addEventListener(
      'touchstart',
      function () {
        if (!isTakeoverActive()) return;
        applyStyles(flydown);
        forcePaintLastItems(flydown);
      },
      { passive: true }
    );

    flydown.addEventListener(
      'touchend',
      function (event) {
        if (!isTakeoverActive()) return;
        if (
          isChromeIOS &&
          event.target &&
          !event.target.closest('a, button, input, select, label, [role="button"]')
        ) {
          var input = getInput();
          if (input && document.activeElement === input) input.blur();
        }
        forceSync();
        forcePaintLastItems(flydown);
      },
      { passive: true }
    );
  });

  document.querySelectorAll('[data-live-search-input]').forEach(function (input) {
    if (input.dataset.mobileScrollBound) return;
    input.dataset.mobileScrollBound = 'true';
    input.addEventListener('blur', forceSync);
  });

  var observer = new MutationObserver(function (mutations) {
    var resultsChanged = false;
    var closed = false;
    var animationChanged = false;

    mutations.forEach(function (mutation) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        if (mutation.target === document.body && !isTakeoverActive()) closed = true;
        if (mutation.target === document.body && isTakeoverActive()) resultsChanged = true;
      }

      if (mutation.type === 'attributes' && mutation.attributeName === 'data-animation') {
        animationChanged = true;
      }

      if (mutation.type === 'attributes' && mutation.attributeName === 'data-animation-state') {
        if (mutation.target.getAttribute('data-animation-state') === 'open') {
          window.requestAnimationFrame(updateDesktopFlydownHeight);
        }
      }

      if (mutation.type === 'childList' && mutation.target.matches('[data-live-search-results]')) {
        resultsChanged = true;
      }
    });

    if (closed) {
      clearStyles(getFlydown());
      return;
    }

    if (animationChanged) {
      var takeover = getTakeover();
      if (takeover && takeover.getAttribute('data-animation')) {
        clearTakeoverTransform();
      } else if (isTakeoverActive()) {
        flattenTakeoverTransform();
        forcePaintLastItems(getFlydown());
      }
    }

    if (resultsChanged) {
      afterResultsLoaded();
      afterDesktopResultsLoaded();
    }
  });

  observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

  document.querySelectorAll('[data-live-search]').forEach(function (el) {
    var results = el.querySelector('[data-live-search-results]');
    if (results) observer.observe(results, { childList: true });
    observer.observe(el, { attributes: true, attributeFilter: ['data-animation', 'class'] });
    var flydown = el.querySelector('[data-live-search-flydown]');
    if (flydown) {
      observer.observe(flydown, { attributes: true, attributeFilter: ['data-animation-state'] });
    }
  });

  scheduleSync();
})();
